import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { notifyErrorCustom } from '../../../../utils/notify';
import { ensureCampaignCreationAllowed } from '../../utils/ensureCampaignCreationAllowed';
import { CAMPAIGN_CHECKOUT_ROUTE } from '../../constants/routes';
import { useAgentChatContext } from '../../context/AgentChatContext';
import { useCampaignRuntimeContext } from '../../context/CampaignRuntimeContext';
import useAgentSessionStore from '../../store/agentSessionStore';
import { getPostQuantityFromMessages } from '../../domain/toolPartSelectors';
import {
  isSessionReadOnly,
  getArtPreviewState,
  resolveChatAttachMode,
  canSubmitArtSingleEdit,
} from '../../domain/campaignStateSelectors';
import { getCampaignArtProgressTitle } from '../../domain/campaignArtProgress';
import { AnimatedPlaceholder } from '../../../../shared/campaign/AnimatedPlaceholder';
import { AttachImageButton } from '../../../../shared/campaign/AttachImageButton';
import { ProductImageSelector } from '../../../../shared/campaign/ProductImageSelector';
import { AttachReferenceArt } from '../../../../components/AttachReferenceArt';
import { buildInitialQueueSteps, buildRegenStrategyQueueSteps } from '../../hooks/campaignSocket/constants';
import { resolveMessageIntegrations, deduplicateMessageIntegrations } from '../../utils/messageIntegrationHelpers';
import { resolveSchedulingLiveState } from '../../utils/schedulingProgressHelpers';
import {
  attachedImagesToFileParts,
  extractAttachedImageUrls,
  getAttachedImagePreviewUrl,
} from '../../utils/attachedImagesToFileParts';
import { buildImagesCompletionMessage } from '../../utils/campaignCompletionMessages';
import {
  logoWidget,
  patchCatalogToolOutput as patchParameterizationStep,
  productImagesWidget,
} from '../../domain/campaignToolCatalog';
import { EditorialLineService } from '../../../../services';
import { usePaywall } from '../../../../contexts/PaywallContext/usePaywall';
import { getArtGenerationAvailability } from '../../../../utils/artConsumption';
import ChatHeader from '../ChatHeader';
import SendButton from './SendButton';
import Message from './Message';
import ErrorMessage from './ErrorMessage';
import { useAutoHideScrollbar } from '../../hooks/useAutoHideScrollbar';
import styles from './styles.module.css';
import ArtLimitProgress from '../../../../components/ArtLimitProgress';
import { getActiveFloatingApproval } from '../../domain/floatingActionSelectors';
import FloatingActionBar from './FloatingActionBar';

export default function Chat({
  isCampaignStartBlocked = false,
  selectedArt = null,
  setSelectedArt = null,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSessionId = useAgentSessionStore((s) => s.activeSessionId);
  const setActiveSession = useAgentSessionStore((s) => s.setActiveSession);
  const {
    sessionDetail,
    messages,
    isChatExpanded,
    isLoading,
    error,
    sendMessage,
    regenerate,
    clearError,
    status,
    setMessages,
  } = useAgentChatContext();
  const {
    runtime,
    setSchedulePreviewArt,
    registerPendingSubmission,
    pendingSubmissions,
  } = useCampaignRuntimeContext();

  const showQueueProgress = runtime.strategyProgress.visible;
  const presentationByMessageId = runtime.presentation?.byMessageId ?? {};
  const floatingPresentation = runtime.presentation?.floating ?? [];
  const queueSteps = runtime.strategyProgress.steps;
  const artSteps = runtime.artSteps;
  const artResults = runtime.content.arts;
  const strategyMarkdown = runtime.content.strategy;
  const isProcessing = runtime.processing.isProcessing;
  const hasMessages = messages.length > 0;
  const isAwaitingFirstToken = status === 'submitted';
  const isResponseStreaming = status === 'streaming';
  const lastMessageIndex = messages.length - 1;
  const messagesContainerRef = useRef(null);
  const { ref: messagesScrollRef, scrollClassName: messagesScrollClassName } =
    useAutoHideScrollbar(messagesContainerRef);
  const isNearBottomRef = useRef(true);
  const [input, setInput] = useState('');
  const [attachArtOpen, setAttachArtOpen] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]);
  const [referenceArt, setReferenceArt] = useState(null);
  const [isProductImageSelectorOpen, setIsProductImageSelectorOpen] =
    useState(false);
  const [editorialLine, setEditorialLine] = useState(null);

  const hasStrategy = runtime.canvas.visible;
  const hasEditorialLine = Boolean(editorialLine);

  useEffect(() => {
    let isMounted = true;

    setEditorialLine(null);

    const loadEditorialLine = async () => {
      try {
        const storedClientData = JSON.parse(
          window.localStorage.getItem('clientData') || 'null',
        );

        const response = storedClientData?.uuid
          ? await EditorialLineService.getEditorialLineByClient(storedClientData.uuid)
          : await EditorialLineService.getEditorialLinebyLogin();

        if (!isMounted) {
          return;
        }

        setEditorialLine(response?.data?.[0]?.bodyJson ?? null);
      } catch {
        if (isMounted) {
          setEditorialLine(null);
        }
      }
    };

    void loadEditorialLine();

    return () => {
      isMounted = false;
    };
  }, [activeSessionId]);

  const placeholderTexts = useMemo(
    () =>
      hasEditorialLine
        ? [
          t('campaignChat.chat.placeholder_brand_1'),
          t('campaignChat.chat.placeholder_brand_2'),
          t('campaignChat.chat.placeholder_brand_3'),
        ]
        : [
          t('campaignChat.chat.placeholder_brand_4'),
          t('campaignChat.chat.placeholder_brand_5'),
          t('campaignChat.chat.placeholder_brand_6'),
        ],
    [hasEditorialLine, t],
  );

  const typingTexts = useMemo(
    () => [
      t('campaignChat.chat.typing_1'),
      t('campaignChat.chat.typing_2'),
      t('campaignChat.chat.typing_3'),
    ],
    [t],
  );

  const strategyProgressTitle = t('chatsocket.chat.creating_strategy');
  const strategyDocumentEditTitle = t(
    'campaignChat.strategyProgress.editingDocument',
    'Editando documento de estratégia...',
  );
  const artProgressTitle = useMemo(() => {
    if (
      runtime.operation?.status === 'processing' &&
      runtime.operation?.stepName &&
      (runtime.operation.toolName === 'editar_arte_campanha' ||
        runtime.operation.toolName === 'editar_artes_lote')
    ) {
      return runtime.operation.stepName;
    }
    return getCampaignArtProgressTitle(
      runtime.content.artStatus,
      runtime.content.artStatus === 'editing' ? 'editing_art' : null,
      t,
    );
  }, [
    runtime.content.artStatus,
    runtime.operation?.status,
    runtime.operation?.stepName,
    runtime.operation?.toolName,
    t,
  ]);
  const initialQueueSteps = useMemo(() => buildInitialQueueSteps(t), [t]);
  const strategyRegenQueueStepsTemplate = useMemo(
    () => buildRegenStrategyQueueSteps(t),
    [t],
  );

  const batchEditMode = runtime.input.batchEditMode;

  const floatingApproval = useMemo(
    () =>
      getActiveFloatingApproval(messages, {
        operation: runtime.operation,
        pendingSubmissions,
        singleArtEditActive: Boolean(
          selectedArt &&
            canSubmitArtSingleEdit({selectedArt, runtime, sessionDetail}),
        ),
      }),
    [
      messages,
      runtime,
      pendingSubmissions,
      selectedArt,
      sessionDetail,
    ],
  );

  const floatingRequiredArts = useMemo(() => {
    if (!floatingApproval) {
      return 0;
    }
    if (floatingApproval.variant === 'strategy') {
      return getPostQuantityFromMessages(messages) ?? 5;
    }
    if (
      floatingApproval.variant === 'arts' ||
      floatingApproval.variant === 'batchConfirm' ||
      floatingApproval.variant === 'batchRejectConfirm'
    ) {
      return Array.isArray(artResults) ? artResults.length : 0;
    }
    return 0;
  }, [floatingApproval, messages, artResults]);

  const integrationContext = useMemo(
    () => ({
      sessionId: activeSessionId,
      messages,
      strategyMarkdown,
      activeOperationToolName: runtime.operation?.toolName ?? null,
      activeOperationFamily: runtime.operation?.family ?? null,
      schedulingLiveState: resolveSchedulingLiveState(runtime),
      artResults,
      onSchedulePreviewArt: setSchedulePreviewArt,
      queueSteps,
      initialQueueSteps,
      strategyRegenQueueStepsTemplate,
      strategyProgressTitle,
      strategyDocumentEditTitle,
      artSteps,
      artProgressTitle,
      artProgressEditingTitle: t(
        'campaignChat.chat.editing_arts',
        'Editando artes...',
      ),
      formatArtPieceLabel: (pieceNumber) =>
        t('campaignChat.artProgress.piece', {
          defaultValue: `Arte ${pieceNumber}`,
          pieceNumber,
        }),
      sessionDetail:
        sessionDetail?.uuid === activeSessionId ? sessionDetail : null,
    }),
    [
      activeSessionId,
      messages,
      hasStrategy,
      strategyMarkdown,
      runtime.operation?.toolName,
      runtime.operation?.family,
      runtime.liveOverlay,
      runtime.snapshot,
      artResults,
      setSchedulePreviewArt,
      queueSteps,
      initialQueueSteps,
      strategyRegenQueueStepsTemplate,
      strategyProgressTitle,
      strategyDocumentEditTitle,
      artSteps,
      artProgressTitle,
      sessionDetail,
      t,
    ],
  );

  const sessionMessages = useMemo(
    () =>
      Array.isArray(sessionDetail?.messages) ? sessionDetail.messages : [],
    [sessionDetail],
  );

  const integrationAnchor = useMemo(
    () => ({
      mode: floatingPresentation.length > 0 ? 'synthetic' : null,
      activeIntegrations: floatingPresentation,
    }),
    [floatingPresentation],
  );

  const [artesCriadasCount, setArtesCriadasCount] = useState(0);
  const [limiteArtesCount, setLimiteArtesCount] = useState(0);

  const updateArtLimits = async () => {
    try {
      const artAvailability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      });
      setArtesCriadasCount(artAvailability?.used ?? 0);
      setLimiteArtesCount(artAvailability?.total ?? 0);
    } catch (error) {
      // noop
    }
  };

  useEffect(() => {
    updateArtLimits();

    const handler = () => updateArtLimits();
    window.addEventListener('user-plan-count-updated', handler);
    return () => window.removeEventListener('user-plan-count-updated', handler);
  }, []);

  useEffect(() => {
    // refresh counts when artResults change (new generated arts)
    updateArtLimits();
  }, [artResults?.length]);

  const showTypingIndicator = isAwaitingFirstToken && !showQueueProgress;
  const isSessionCompleted = isSessionReadOnly(sessionDetail);
  const batchRejectMode = runtime.input.batchRejectMode;
  const allowArtSingleEdit = Boolean(
    selectedArt &&
    canSubmitArtSingleEdit({ selectedArt, runtime, sessionDetail }),
  );
  const isInputBlocked =
    runtime.input.disabled && !allowArtSingleEdit && !batchEditMode && !batchRejectMode;
  const isInputLocked = isInputBlocked && !selectedArt;

  const effectiveAttachMode = useMemo(() => {
    if (selectedArt && allowArtSingleEdit) {
      return 'referenceArt';
    }
    return (
      resolveChatAttachMode(messages, {
        selectedArt,
        isParameterizationComplete: hasStrategy,
        sessionMessages,
      }) ?? runtime.input.attachMode
    );
  }, [
    selectedArt,
    allowArtSingleEdit,
    messages,
    hasStrategy,
    sessionMessages,
    runtime.input.attachMode,
  ]);

  const showAttachControls =
    effectiveAttachMode === 'referenceArt'
      ? Boolean(selectedArt)
      : Boolean(effectiveAttachMode) &&
        !(effectiveAttachMode === 'logo' && referenceArt);

  const hasAttachedImages =
    effectiveAttachMode === 'referenceArt' || effectiveAttachMode === 'logo'
      ? Boolean(referenceArt)
      : effectiveAttachMode === 'images'
        ? attachedImages.length > 0
        : false;

  const showFloatingActionBar =
    isChatExpanded &&
    Boolean(floatingApproval) &&
    !isResponseStreaming &&
    !hasAttachedImages;

  const isAttachDisabled =
    isLoading ||
    isInputBlocked ||
    !effectiveAttachMode ||
    isSessionReadOnly(sessionDetail);

  const { openPaywall, closePaywall } = usePaywall();

  const submitInput = useCallback(async () => {
    if (
      (!input.trim() && !hasAttachedImages) ||
      isLoading ||
      isInputBlocked ||
      isCampaignStartBlocked
    ) {
      return;
    }

    if (!activeSessionId) {
      const allowed = await ensureCampaignCreationAllowed();
      if (!allowed) {
        notifyErrorCustom(t('campaignChat.errors.insufficientArtsToStart'));
        return;
      }
    }

    const awaitingImagesPart = productImagesWidget.findPart(messages, [
      'awaiting_selection',
    ]);

    if (awaitingImagesPart) {
      const toolCallId = productImagesWidget.getTrackingId(awaitingImagesPart);
      const imageUrls = extractAttachedImageUrls(attachedImages);

      if (toolCallId) {
        registerPendingSubmission(toolCallId);
      }

      void sendMessage(buildImagesCompletionMessage(imageUrls));
      setInput('');
      setAttachedImages([]);
      setReferenceArt(null);
      setAttachArtOpen(false);
      return;
    }

    if (selectedArt && (input.trim() || referenceArt)) {
      if (!activeSessionId) {
        notifyErrorCustom(
          t(
            'campaignChat.errors.sessionRequired',
            'Selecione ou inicie uma sessão de campanha antes de editar.',
          ),
        );
        return;
      }

      const availability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      });
      if (!availability.hasAvailableArts) {
        openPaywall({
          reason: 'art_generation_limit_reached',
          returnTo: window.location.pathname,
          onDismiss: async () => {
            window.dispatchEvent(
              new CustomEvent('user-plan-count-updated'),
            );
            closePaywall();
          },
        });
        return;
      }

      const pieceNumber = selectedArt.pieceNumber ?? selectedArt.piece_number;
      const instruction =
        input.trim() ||
        'Use a imagem de referência enviada pelo usuário para orientar a edição da arte.';
      const files = referenceArt
        ? attachedImagesToFileParts([], referenceArt)
        : undefined;

      void sendMessage({
        text: `Editar arte ${pieceNumber}: ${instruction}`.trim(),
        files: files?.length > 0 ? files : undefined,
      });
      setInput('');
      setReferenceArt(null);
      setSelectedArt?.(null);
      return;
    }

    if (effectiveAttachMode === 'logo' && referenceArt) {
      const awaitingLogoPart = logoWidget.findPart(messages, {
        statuses: ['awaiting_selection'],
      });
      const toolCallId = logoWidget.getTrackingId(awaitingLogoPart);
      const files = attachedImagesToFileParts([], referenceArt);

      if (toolCallId) {
        setMessages((currentMessages) =>
          patchParameterizationStep(
            currentMessages,
            logoWidget.toolName,
            toolCallId,
            { component: 'logoUpload' },
          ),
        );
      }

      void sendMessage({
        text: input.trim() || undefined,
        files: files.length > 0 ? files : undefined,
      });
      setInput('');
      setAttachedImages([]);
      setReferenceArt(null);
      setAttachArtOpen(false);
      return;
    }

    const files = attachedImagesToFileParts(attachedImages, null);
    void sendMessage({
      text: input.trim() || undefined,
      files: files.length > 0 ? files : undefined,
    });
    setInput('');
    setAttachedImages([]);
    setReferenceArt(null);
    setAttachArtOpen(false);
  }, [
    input,
    hasAttachedImages,
    attachedImages,
    referenceArt,
    isLoading,
    isInputBlocked,
    isCampaignStartBlocked,
    effectiveAttachMode,
    messages,
    sendMessage,
    setMessages,
    activeSessionId,
    t,
    selectedArt,
    setSelectedArt,
    openPaywall,
    closePaywall,
  ]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    submitInput();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitInput();
    }
  };

  const handleReferenceArtSelect = useCallback((imageData) => {
    if (imageData) {
      setReferenceArt({
        ...imageData,
        fileDataBase64: imageData.base64,
      });
    } else {
      setReferenceArt(null);
    }
    setAttachArtOpen(false);
  }, []);

  const handleProductImagesSave = useCallback((images) => {
    setAttachedImages(images || []);
    setIsProductImageSelectorOpen(false);
  }, []);

  const attachOptions = useMemo(() => {
    if (effectiveAttachMode === 'logo') {
      return [
        {
          id: 'reference',
          label: t('chatsocket.buttons.logotipo'),
          onSelect: (imageData) => handleReferenceArtSelect(imageData),
        },
      ];
    }

    if (effectiveAttachMode === 'images') {
      return [
        {
          id: 'type',
          label: t('chatsocket.buttons.images'),
          openModalOnly: true,
          onSelect: () => setIsProductImageSelectorOpen(true),
        },
      ];
    }

    if (effectiveAttachMode === 'referenceArt') {
      return [
        {
          id: 'reference',
          label: t('chatsocket.buttons.attach_reference_art_label'),
          onSelect: (imageData) => handleReferenceArtSelect(imageData),
        },
      ];
    }

    return [];
  }, [effectiveAttachMode, t, handleReferenceArtSelect]);

  useEffect(() => {
    if (effectiveAttachMode === 'logo' || effectiveAttachMode === 'referenceArt') {
      setAttachedImages([]);
      setIsProductImageSelectorOpen(false);
      return;
    }

    if (effectiveAttachMode === 'images') {
      setReferenceArt(null);
      return;
    }

    setAttachedImages([]);
    setReferenceArt(null);
    setAttachArtOpen(false);
    setIsProductImageSelectorOpen(false);
  }, [effectiveAttachMode]);

  useEffect(() => {
    if (batchEditMode || batchRejectMode) {
      setSelectedArt?.(null);
      setReferenceArt(null);
    }
  }, [batchEditMode, batchRejectMode, setSelectedArt]);

  const handleRetry = () => {
    clearError();
    regenerate();
  };

  useEffect(() => {
    if (!isChatExpanded) {
      setAttachArtOpen(false);
      setAttachedImages([]);
      setReferenceArt(null);
      setIsProductImageSelectorOpen(false);
    }
  }, [isChatExpanded]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      isNearBottomRef.current =
        scrollHeight - scrollTop - clientHeight < 100;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!hasMessages || !isNearBottomRef.current) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    const frame = window.requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    messages,
    isLoading,
    hasMessages,
    queueSteps,
    showQueueProgress,
    integrationAnchor.activeIntegrations,
    showTypingIndicator,
  ]);

  const showPlaceholderOverlay =
    !input.trim() && !isChatExpanded && !isInputLocked;

  const isInputActive = !(
    isLoading ||
    isInputBlocked ||
    isSessionCompleted ||
    isCampaignStartBlocked
  );

  const preview = getArtPreviewState(messages, {
    socketResults: artResults,
    socketStatus: runtime.content.artStatus,
    socketProgress: runtime.content.artProgress,
    sessionDetail,
  });

  const shouldShowTopProgress = Boolean(selectedArt) || (isInputActive && preview.showArtsTab);

  const getMessageIntegrations = useCallback(
    (msg, index) => {
      const perMessageIntegrations = resolveMessageIntegrations(
        msg,
        integrationContext,
      );
      const activeIntegrations = presentationByMessageId[msg.id] ?? [];

      if (perMessageIntegrations.length === 0 && activeIntegrations.length === 0) {
        return null;
      }

      const dedupedPresentation = deduplicateMessageIntegrations(
        perMessageIntegrations,
        activeIntegrations,
      );

      return [...perMessageIntegrations, ...dedupedPresentation];
    },
    [presentationByMessageId, integrationContext],
  );

  const handleIntegrationSideEffect = useCallback(
    async (variant, action) => {
      if (variant === 'arts' && action === 'EDIT') {
        setSelectedArt?.(null);
      }
      if (variant === 'finalActions') {
        if (action === 'SELECT_PLAN') {
          navigate(CAMPAIGN_CHECKOUT_ROUTE);
        }
        if (action === 'NEW_CAMPAIGN') {
          const allowed = await ensureCampaignCreationAllowed();
          if (!allowed) {
            notifyErrorCustom(t('campaignChat.errors.insufficientArtsToStart'));
            return;
          }
          setActiveSession(null);
          setMessages([]);
        }
      }
    },
    [navigate, setActiveSession, setMessages, setSelectedArt, t],
  );

  const wrapperClass = (() => {
    if (!isChatExpanded) {
      return styles.chatWrapperSmall;
    }
    if (hasStrategy) {
      return styles.chatWrapperExpanded;
    }
    return styles.chatWrapperActive;
  })();

  const inputContainerClass = (() => {
    if (!isChatExpanded) {
      return styles.inputContainerSmall;
    }
    if (hasStrategy) {
      return styles.inputContainerExpanded;
    }
    return '';
  })();

  return (
    <div className={`${styles.chatWrapper} ${wrapperClass}`}>
      {isChatExpanded && <ChatHeader />}
      {isChatExpanded && (
        <div
          ref={messagesScrollRef}
          className={`${styles.messagesContainer} ${styles.messagesContainerVisible} ${messagesScrollClassName} ${showFloatingActionBar ? styles.messagesContainerWithFloatingBar : ''}`}
        >
          {!hasMessages && (
            <div className={styles.welcomeMessage}>
              <p>{t('campaignChat.welcome.message', 'Olá! Vamos criar sua campanha. Me conte sobre o que você quer divulgar.')}</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <Message
              key={msg.id || i}
              message={msg}
              isStreaming={
                isResponseStreaming &&
                i === lastMessageIndex &&
                msg.role === 'assistant'
              }
              messageIntegrations={getMessageIntegrations(msg, i)}
              onIntegrationSideEffect={handleIntegrationSideEffect}
            />
          ))}
          {integrationAnchor.mode === 'synthetic' && (
            <Message
              key="integration-synthetic"
              message={{ role: 'assistant', id: 'integration-synthetic', parts: [] }}
              messageIntegrations={integrationAnchor.activeIntegrations}
              isIntegrationOnly
              onIntegrationSideEffect={handleIntegrationSideEffect}
            />
          )}
          {showTypingIndicator && (
            <div className={styles.typingContainer}>
              <AnimatedPlaceholder texts={typingTexts} typingSpeed={40} />
            </div>
          )}
          {error && !isCampaignStartBlocked && (
            <ErrorMessage
              onRetry={
                /session has been completed/i.test(error?.message || '') ||
                  /Limite de \d+ gerações de estratégia atingido/i.test(
                    error?.message || '',
                  )
                  ? undefined
                  : handleRetry
              }
              message={
                /session has been completed/i.test(error?.message || '')
                  ? t('campaignChat.error.sessionCompleted')
                  : /Limite de \d+ gerações de estratégia atingido/i.test(
                    error?.message || '',
                  )
                    ? t('campaignChat.error.strategyGenerationLimit')
                    : undefined
              }
            />
          )}
        </div>
      )}
      <form className={styles.inputForm} onSubmit={handleSubmit}>
        <div className={`${styles.inputContainer} ${inputContainerClass}`}>
          {showFloatingActionBar && (
            <FloatingActionBar
              variant={floatingApproval.variant}
              toolCallId={floatingApproval.toolCallId}
              requiredArts={floatingRequiredArts}
              onSideEffect={handleIntegrationSideEffect}
              shifted={shouldShowTopProgress}
            />
          )}
          {shouldShowTopProgress && (
            <div className={styles.progressOverlay}>
              <ArtLimitProgress artesCriadas={artesCriadasCount ?? artResults?.length ?? 0} limiteArtes={limiteArtesCount} layout="chat" checkoutPath={CAMPAIGN_CHECKOUT_ROUTE} />
            </div>
          )}
          {isChatExpanded && hasAttachedImages && (
            <div className={`${styles.imagesPreview} ${shouldShowTopProgress ? styles.imagesPreviewShifted : ''}`}>
              {effectiveAttachMode === 'images' &&
                attachedImages.map((image, index) => {
                  const imageUrl = getAttachedImagePreviewUrl(image);
                  if (!imageUrl) return null;

                  return (
                    <div key={image.id || `attached-${index}`} className={styles.imagePreviewItem}>
                      <img
                        src={imageUrl}
                        alt={t('chatsocket.chat.preview')}
                        className={styles.previewImage}
                      />
                      <button
                        type="button"
                        className={styles.removeImageButton}
                        onClick={() =>
                          setAttachedImages((current) =>
                            current.filter((_, itemIndex) => itemIndex !== index),
                          )
                        }
                        title={t('chatsocket.buttons.remove_image')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              {(effectiveAttachMode === 'logo' ||
                effectiveAttachMode === 'referenceArt') &&
                referenceArt &&
                getAttachedImagePreviewUrl(referenceArt) && (
                <div className={styles.imagePreviewItemOrange}>
                  <img
                    src={getAttachedImagePreviewUrl(referenceArt)}
                    alt={t('chatsocket.chat.preview')}
                    className={styles.previewImage}
                  />
                  <button
                    type="button"
                    className={styles.removeImageButton}
                    onClick={() => setReferenceArt(null)}
                    title={t('chatsocket.buttons.remove_image')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
          {showPlaceholderOverlay && (
            <div className={styles.placeholderOverlay}>
              <AnimatedPlaceholder texts={placeholderTexts} typingSpeed={40} />
            </div>
          )}
          <textarea
            className={`${styles.input} ${!isChatExpanded ? styles.inputSmall : ''}`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isChatExpanded
                ? isSessionCompleted
                  ? t('campaignChat.chat.sessionCompleted')
                  : allowArtSingleEdit
                    ? t(
                      'campaignChat.chat.placeholder_single_art_edit',
                      'Descreva a edição desta arte...',
                    )
                    : batchEditMode
                      ? t(
                        'campaignChat.chat.placeholder_batch_edit',
                        'Descreva as alterações para aplicar em todas as artes...',
                      )
                      : batchRejectMode
                        ? t(
                          'campaignChat.chat.placeholder_batch_reject',
                          'Descreva o motivo da reprovação e o que deseja alterar...',
                        )
                        : isInputLocked
                          ? t('campaignChat.chat.inputLocked', 'Aguardando sua seleção...')
                          : t('chatsocket.chat.placeholder_default')
                : ''
            }
            disabled={
              isLoading ||
              isInputBlocked ||
              isSessionCompleted ||
              isCampaignStartBlocked
            }
            rows={2}
          />
          <div
            className={`${styles.inputActions} ${!isChatExpanded ? styles.inputActionsCompact : ''
            }`}
          >
            {isChatExpanded && showAttachControls && (
              <div className={styles.inputActionsDivider}>
                <div className={styles.attachControls}>
                  <div className={styles.attachWrapper}>
                    <AttachImageButton
                      visible
                      disabled={isAttachDisabled}
                      onClick={() => {
                        if (isAttachDisabled) return;
                        setAttachArtOpen(!attachArtOpen);
                      }}
                    />
                  </div>
                  <AttachReferenceArt
                    open={attachArtOpen}
                    setOpen={setAttachArtOpen}
                    onImageSelect={handleReferenceArtSelect}
                    verticalLayout={true}
                    hideExpandButton={true}
                    menuPosition="top"
                    menuClassName={styles.chatAttachMenu}
                    disabled={isAttachDisabled}
                    options={attachOptions}
                    acceptedFormats="image/*"
                    maxFileSize={10 * 1024 * 1024}
                    showPreview={false}
                  />
                </div>
              </div>
            )}
            <SendButton
              disabled={
                isLoading ||
                isInputBlocked ||
                isCampaignStartBlocked ||
                (!input.trim() && !hasAttachedImages)
              }
              className={styles.sendButton}
            />
          </div>
        </div>
      </form>
      <ProductImageSelector
        isOpen={isProductImageSelectorOpen}
        onClose={() => setIsProductImageSelectorOpen(false)}
        onSave={handleProductImagesSave}
        preselectedImages={attachedImages}
        maxImages={5}
        fixedPosition
      />
    </div>
  );
}
